/**
 * API client for per-tank dashboard layout persistence.
 *
 * The server stores at most one layout document per tank and wraps every
 * GET/PUT response in a `{ layout }` envelope where `layout` is null when the
 * tank has never been customized (or was reset via DELETE).
 */
import { buildApiUrl } from './config';
import { apiClient } from './client';
import type { DashboardLayout } from '../hooks/useDashboardLayout';

async function parseError(response: Response): Promise<Error> {
  const error = await response.json().catch(() => ({ detail: response.statusText }));
  return new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
}

export async function getDashboardLayout(tankId: string): Promise<DashboardLayout | null> {
  const response = await apiClient(
    buildApiUrl(`/api/tanks/${encodeURIComponent(tankId)}/dashboard-layout`),
    { method: 'GET' }
  );
  if (!response.ok) throw await parseError(response);
  const data = await response.json();
  return data.layout ?? null;
}

export async function putDashboardLayout(
  tankId: string,
  layout: DashboardLayout
): Promise<DashboardLayout | null> {
  const response = await apiClient(
    buildApiUrl(`/api/tanks/${encodeURIComponent(tankId)}/dashboard-layout`),
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(layout),
    }
  );
  if (!response.ok) throw await parseError(response);
  const data = await response.json();
  return data.layout ?? null;
}

export async function deleteDashboardLayout(tankId: string): Promise<void> {
  const response = await apiClient(
    buildApiUrl(`/api/tanks/${encodeURIComponent(tankId)}/dashboard-layout`),
    { method: 'DELETE' }
  );
  if (!response.ok) throw await parseError(response);
}

/** React Query key for a tank's dashboard layout. */
export const dashboardLayoutQueryKey = (tankId: string): [string, string] => [
  'dashboard-layout',
  tankId,
];
